// 職責：後端散落於 model 註解／舊前端 const 的列舉，集中為單一事實來源（值與顯示標籤分離）

/*
 * 本檔對應文檔 01 §12「列舉值總表」。
 * 為何要集中：後端把同一組列舉寫在 controller、EF model 註解、舊前端 const 三處，
 * 且有數處彼此矛盾（見下方 ⚠️ 註記）。集中後，矛盾只需在此處裁決一次。
 *
 * 慣例：
 *   1. 值用 `as const` 物件；同名 type 由值推導，避免 TS enum 的執行期產物與雙向映射。
 *   2. 顯示標籤一律 `xxxLabel: Record<Type, string>` —— 少一個鍵就編譯失敗，
 *      這是「新增列舉值時忘了補標籤」的編譯期防線。
 *   3. 標籤此處先以繁體中文寫死；零售模式的詞彙替換由 i18n 層覆寫（文檔 06 §7），
 *      元件不得直接依賴這些字串做業務判斷。
 */

import type { Terms } from '@/domain/rules/wording'
import { isCustomMenuGroup, type CustomMenuGroupNo } from '@/domain/rules/customMenuGroup'

/* ────────────────────────────── M3 菜單 ────────────────────────────── */

/**
 * 渠道／點餐平台（`merchant_good`、`merchant_goods_category`、`merchant_period`、
 * `merchant_package` 的 `menu_group`）。
 * ⚠️ 新增資料列時必填，漏填會讓資料寫入成功卻從清單消失（文檔 01 §2.2、地雷 #6）。
 * ⚠️ 待確認 Q9：哪些渠道在本次上線時真的啟用。
 *
 * ★ 2026-08-17 Peter 給出的權威值域（`kiosk/kiosk-android/doc/period-req.md`）：
 *     0 堂食、1 外賣、2 MF、3 快速落單機制、4 外賣麵包專櫃、5 推介餐品、6 戶戶送、
 *     7 Website、8 foodpanda、9 keeta、**10 自助售賣**、15 澳覓、16 閃蜂、
 *     20 四時時段、21 福食
 *
 *   本表只收錄目前用得着的值。**未收錄的 6 / 20 / 21 有一個已知副作用**：
 *   `isMenuGroup` 對它們回 false，於是 `toPeriod`／`toCategory` 把 `menu_group`
 *   讀成 null（api/schemas/period.ts:176、category.ts:91）——
 *   時段那邊 `periodChannel(null)` 會當成堂食（domain/rules/periodCoverage.ts:143，
 *   因為後端就是 `(p.menu_group ?? 0) == 0`），即一條 `menu_group = 21` 的福食時段
 *   會被本後台算進堂食的覆蓋數字。真的有門店在用那三個值時，補進本表即可修正
 *   （補完編譯器會逼你補齊 `menuGroupLabel` 與 `PRICING_MODE`）。
 */
export const MenuGroup = {
  DineIn: 0,
  Takeaway: 1,
  MFood: 2,
  QuickOrder: 3,
  BreadCounter: 4,
  MobileRecommend: 5,
  Website: 7,
  FoodPanda: 8,
  Keeta: 9,
  /**
   * 自助售賣（浸會 21.5" 細自助機 + 大雪櫃）。
   * 客人在機上落單付款後自行到雪櫃取貨 —— 餐牌與大自助機**完全不同**，
   * 所以它需要自己一組時段，而不是共用堂食／外賣那兩組。
   */
  SelfService: 10,
  /**
   * 撇數（南園 802「全包宴」這類整圍議好價、不會逐道菜落單的餐）。
   *
   * 這一組**不是給客人落單的渠道**，而是一組「部門餐品」的收納櫃：埋單之後收銀員
   * 按「撇數」，就是在這一組內選擇部門、逐個輸入金額，加到主餐下面做套餐子項，
   * 令部門營業報表收到數（詳見 scpos/docs/20260822-rq-review_zh.md §1.2）。
   *
   * ★ 它一定要是自己一組 menu_group：落單畫面查詢的是檯的 menu_group（一般 0），
   *   所以 11 這一組天然不會出現在餐牌。**這就是它能隔離得住的唯一機制** ——
   *   一旦這個值在本表消失，`isMenuGroup` 就會回 false、`toPeriod` 讀成 null、
   *   `fromPeriod` 照樣寫回 null（`compact` 只丟 undefined、不丟 null），
   *   於是有人在後台只改時段名再儲存，`menu_group` 就變 NULL →
   *   後端 `(p.menu_group ?? 0) == 0` → **六件部門餐品即時跌回堂食餐牌**，
   *   客人按得到「廚部 $0」。所以這一行是承重的，不是裝飾。
   */
  DeptSplit: 11,
  Aomi: 15,
  ShanFeng: 16,
} as const
/** 上表那批有固定名稱的渠道 */
export type BuiltinMenuGroup = (typeof MenuGroup)[keyof typeof MenuGroup]
/**
 * 渠道 = 固定渠道 ∪ 門店自定義渠道（30–39，2026-09-25，例：801「燒味」）。
 *
 * 🚨 自定義渠道**一定要**算進 `MenuGroup`（`isMenuGroup` 認得）：理由與上面撇數那段一樣 ——
 *    認不到就會令 `toPeriod` 把 `menu_group` 讀成 null，有人只改時段名再儲存，
 *    30 就被寫成 NULL → 後端 `(p.menu_group ?? 0) == 0` → 燒味時段即時跌進堂食餐牌。
 * ★ 名稱由門店自己在「供應時段」頁設定，所以不放在 `menuGroupLabel` 表內，
 *   經 `menuGroupLabelOf(group, terms)` 的門店詞彙取得（見 domain/rules/customMenuGroup）。
 */
export type MenuGroup = BuiltinMenuGroup | CustomMenuGroupNo

/**
 * ⚠️ `MobileRecommend` 這格刻意保留「餐品」—— 它是**餐飲預設**，不是漏改。
 *    面向使用者時一律走下面 `menuGroupLabelOf()`，它會按門店詞彙換成「商品」。
 */
export const menuGroupLabel: Record<BuiltinMenuGroup, string> = {
  [MenuGroup.DineIn]: '堂食',
  [MenuGroup.Takeaway]: '外賣自取',
  [MenuGroup.MFood]: 'MFood',
  [MenuGroup.QuickOrder]: '快速落單',
  [MenuGroup.BreadCounter]: '外賣麵包專櫃',
  [MenuGroup.MobileRecommend]: '手機推介餐品',
  [MenuGroup.Website]: 'Website',
  [MenuGroup.FoodPanda]: 'FoodPanda',
  [MenuGroup.Keeta]: 'Keeta',
  [MenuGroup.SelfService]: '自助售賣',
  [MenuGroup.DeptSplit]: '撇數',
  [MenuGroup.Aomi]: '澳覓',
  [MenuGroup.ShanFeng]: '閃峰',
}

/**
 * 帶門店詞彙的版本。★ 十二個渠道之中只有 `MobileRecommend`（「手機推介餐品」）含「餐品」，
 * 其餘原樣回傳。零售店不應該在任何一個下拉／標籤中見到「餐品」兩個字（doc 06 §7）。
 */
export function menuGroupLabelOf(group: MenuGroup, terms: Terms): string {
  // 自定義渠道：名稱是門店自己起的（例：燒味），屬於門店詞彙；沒有名稱就顯示「自定義 30」
  if (isCustomMenuGroup(group))
    return terms.customMenuGroupName?.(group) ?? `自定義 ${String(group)}`
  return group === MenuGroup.MobileRecommend ? `手機推介${terms.goods}` : menuGroupLabel[group]
}

/** `merchant_good.status`：1=正常、0=下架 */
export const GoodsStatus = {
  OffShelf: 0,
  Normal: 1,
} as const
export type GoodsStatus = (typeof GoodsStatus)[keyof typeof GoodsStatus]

export const goodsStatusLabel: Record<GoodsStatus, string> = {
  [GoodsStatus.OffShelf]: '下架',
  [GoodsStatus.Normal]: '正常',
}

/**
 * `merchant_good.unit`（店庫 DICT_GOODS_UNIT）。
 * 8 = 斤、9 = 兩：海鮮按重量計價用（與 cost_price = -1 同時出現時，price 是每斤／每兩單價），
 * 見 `domain/rules/seafood.ts`。未列入的值讀入時當 null，但整列合併寫回時保留原值。
 */
export const GoodsUnit = {
  Portion: 1,
  Piece: 2,
  Catty: 8,
  Tael: 9,
} as const
export type GoodsUnit = (typeof GoodsUnit)[keyof typeof GoodsUnit]

export const goodsUnitLabel: Record<GoodsUnit, string> = {
  [GoodsUnit.Portion]: '份',
  [GoodsUnit.Piece]: '個',
  [GoodsUnit.Catty]: '斤',
  [GoodsUnit.Tael]: '兩',
}

/** `merchant_good.attribute`（商品屬性）*/
export const GoodsAttribute = {
  Food: 0,
  HotDrink: 1,
  ColdDrink: 2,
  SpecialDrink: 3,
  Alcohol: 4,
  Misc: 5,
  Tips: 6,
  SalesVariance: 7,
} as const
export type GoodsAttribute = (typeof GoodsAttribute)[keyof typeof GoodsAttribute]

export const goodsAttributeLabel: Record<GoodsAttribute, string> = {
  [GoodsAttribute.Food]: '食品',
  [GoodsAttribute.HotDrink]: '熱飲',
  [GoodsAttribute.ColdDrink]: '凍飲',
  [GoodsAttribute.SpecialDrink]: '特飲',
  [GoodsAttribute.Alcohol]: '酒',
  [GoodsAttribute.Misc]: '雜項',
  [GoodsAttribute.Tips]: '小費',
  [GoodsAttribute.SalesVariance]: '營業差額',
}

/** `merchant_good.is_setmeal`：0=單品、1=套餐 */
export const GoodsSetmealFlag = {
  Single: 0,
  Setmeal: 1,
} as const
export type GoodsSetmealFlag = (typeof GoodsSetmealFlag)[keyof typeof GoodsSetmealFlag]

export const goodsSetmealFlagLabel: Record<GoodsSetmealFlag, string> = {
  [GoodsSetmealFlag.Single]: '單品',
  [GoodsSetmealFlag.Setmeal]: '套餐',
}

/**
 * `merchant_setmeal.is_main`：套餐明細在套餐中的角色。
 * ⚠️ 這是三態 int，不是布林 —— 名字帶 `is_` 容易被誤判（文檔 07 §5.1）。
 */
export const SetmealMainType = {
  Main: 0,
  Side: 1,
  Addition: 2,
} as const
export type SetmealMainType = (typeof SetmealMainType)[keyof typeof SetmealMainType]

export const setmealMainTypeLabel: Record<SetmealMainType, string> = {
  [SetmealMainType.Main]: '主食',
  [SetmealMainType.Side]: '副品',
  [SetmealMainType.Addition]: '加配',
}

/* ────────────────────────────── M5 套餐搭配 ────────────────────────────── */

/**
 * `merchant_package.flag`：套餐搭配的分桶鍵（文檔 01 §5.1）。
 * ⚠️ 後端 model 註解寫的是「0:商品 1:口味」，與實際用法不符 —— 以本表為準。
 * ⚠️ 待確認 Q6：`flag = 4` 用途不明。舊系統無任何 UI 產生此值，
 *    但既有資料可能存在，因此保留於型別中，僅在讀取時能被正確辨識與顯示。
 */
export const PackageFlag = {
  ComboSet: 0,
  Flavour: 1,
  AddonItem: 2,
  AddonDrink: 3,
  Unknown4: 4,
} as const
export type PackageFlag = (typeof PackageFlag)[keyof typeof PackageFlag]

export const packageFlagLabel: Record<PackageFlag, string> = {
  [PackageFlag.ComboSet]: '餐點組合',
  [PackageFlag.Flavour]: '口味（已停用）',
  [PackageFlag.AddonItem]: '加購單品',
  [PackageFlag.AddonDrink]: '加購飲品',
  [PackageFlag.Unknown4]: '未知類型（待確認）',
}

/** 新後台可建立的分桶（停用的口味與用途不明的 4 不在此列，UI 只讀不寫）*/
export const editablePackageFlags = [
  PackageFlag.ComboSet,
  PackageFlag.AddonItem,
  PackageFlag.AddonDrink,
] as const

/* ────────────────────────────── M7 收款與優惠 ────────────────────────────── */

/*
 * `merchant_payment_method.flag`（付款屬性）。
 *
 * ⚠️ 2026-08-07 更正：本列舉原本只到 3，但**開店範本 seed 本身就有 5 同 6**
 *    （`webapi/sql/store-template/food.sql:26612` 起：id 113 flag=5「八達通」、
 *    id 114 flag=6「八達通」pay_id 2001、id 6/118 支付寶、7/119 銀聯…）。
 *    即是說「值域只到 3」由頭到尾都是錯的。
 *
 * ★★ 2026-08-29 值域確認：`flag` **不是分類，是「這個付款方式在 POS 哪張清單出現」**。
 *    後端唯一讀它的地方是 `merchant_payment_methodController.cs:29-33`：
 *
 *        GET api/merchant_payment_method/flag/{flag}
 *          → .Where(p => (p.is_enabled ?? false) && (p.flag == 0 || p.flag == flag))
 *
 *    即是 **flag=0 是萬用（每張清單都出）**，其餘只出現在同號的那張清單。
 *    POS 端哪個畫面查詢哪個號，實證：
 *      - `scpos/packages/vue/src/utils/pay-method-kind.ts:112-133` `resolvePayFlag()`
 *        5 = 改腳／退貨／換貨補退（純記賬，不經終端機）
 *        6 = 一般埋單（KPay）
 *        7 = 埋單（YeahPay）—— 每行的 `code` 就是 YeahPay 指令
 *      - `scpos/.../settings.vue:2337` 及 `kiosk/.../Settings.vue:266`
 *        `PROVIDER_FLAG = { kpay: 6, yeahpay: 7 }`
 *
 * 🚨🚨 因此 **flag 是 NULL 的列，POS 永遠都撈不到**（`null == 0` 與 `null == flag`
 *      在 SQL 兩邊都不成立）。新增付款方式時沒有寫 flag，後台看得見、POS 完全沒有 ——
 *      這就是「新支付方式顯示不到」的成因。新增一定要有值，不知道就選 0。
 */
export const PaymentFlag = {
  /** 萬用：每一張清單（改腳／KPay 埋單／YeahPay 埋單）都會出現 */
  Universal: 0,
  Storellet: 1,
  DiscountPromotion: 2,
  AirlineVoucher: 3,
  /** 改腳／退貨／換貨補退專用清單：純記賬，不會經終端機 */
  Bookkeeping: 5,
  /** 一般埋單（KPay）清單 */
  SettleKPay: 6,
  /** 埋單（YeahPay）清單：每行的 `code` 就是 YeahPay 指令 */
  SettleYeahPay: 7,
} as const
export type PaymentFlag = (typeof PaymentFlag)[keyof typeof PaymentFlag]

export const paymentFlagLabel: Record<PaymentFlag, string> = {
  [PaymentFlag.Universal]: '全部清單（萬用）',
  [PaymentFlag.Storellet]: 'Storellet',
  [PaymentFlag.DiscountPromotion]: '折扣推廣',
  [PaymentFlag.AirlineVoucher]: '航空券',
  [PaymentFlag.Bookkeeping]: '改腳／退貨（記賬）',
  [PaymentFlag.SettleKPay]: '埋單 · KPay',
  [PaymentFlag.SettleYeahPay]: '埋單 · YeahPay',
}

/**
 * 下拉選項。次序按「日常會選哪個」排，不按數值排 ——
 * 1／2／3 是舊制，新開的店見不到，放在最後並標明。
 */
export const paymentFlagOptions: readonly {
  value: PaymentFlag
  label: string
  hint: string
  legacy?: boolean
}[] = [
  {
    value: PaymentFlag.Universal,
    label: `0 — ${paymentFlagLabel[PaymentFlag.Universal]}`,
    hint: '每一張付款清單都會出現。不肯定選擇哪一個的時候，選擇這個最安全。',
  },
  {
    value: PaymentFlag.SettleKPay,
    label: `6 — ${paymentFlagLabel[PaymentFlag.SettleKPay]}`,
    hint: '只在走 KPay 的埋單畫面出現（門店未開 YeahPay 時的一般埋單清單）。',
  },
  {
    value: PaymentFlag.SettleYeahPay,
    label: `7 — ${paymentFlagLabel[PaymentFlag.SettleYeahPay]}`,
    hint: '只在走 YeahPay 的埋單畫面出現；該行的「代號 code」就是 YeahPay 指令。',
  },
  {
    value: PaymentFlag.Bookkeeping,
    label: `5 — ${paymentFlagLabel[PaymentFlag.Bookkeeping]}`,
    hint: '只在改腳／退貨／換貨補退畫面出現。純記賬，POS 不會叫終端機。',
  },
  {
    value: PaymentFlag.Storellet,
    label: `1 — ${paymentFlagLabel[PaymentFlag.Storellet]}`,
    hint: '舊制值，新店不會用到。',
    legacy: true,
  },
  {
    value: PaymentFlag.DiscountPromotion,
    label: `2 — ${paymentFlagLabel[PaymentFlag.DiscountPromotion]}`,
    hint: '舊制值，新店不會用到。',
    legacy: true,
  },
  {
    value: PaymentFlag.AirlineVoucher,
    label: `3 — ${paymentFlagLabel[PaymentFlag.AirlineVoucher]}`,
    hint: '舊制值，新店不會用到。',
    legacy: true,
  },
]

/**
 * `merchant_payment_method.pay_id`：跨店通用的業界代碼，**不是自增主鍵**。
 * 清單非窮舉（各店可能有未列的代碼），故不定義為封閉型別，只提供顯示名對照。
 */
export const knownPayIdLabel: Readonly<Record<number, string>> = {
  0: '現金',
  2: 'VISA',
  3: 'MASTER',
  4: '微信支付',
  5: '支付寶',
  6: '銀聯',
  10: 'AE',
  23: 'JCB',
  29: 'Bocpay',
  101: '八達通',
}

/* ────────────────────────────── M8 廚房與打印 ────────────────────────────── */

/**
 * `merchant_production_dept.dept_type`。
 *
 * ★★ 2026-07-23 以三個 UAT 庫實測，Q5 有了證據（原「前端 vs 後端」二選一是假問題）：
 *   實際值域只有 `0 / 1 / 2 / 3 / NULL`，其中 **`0` 佔 55.6%（10/18 列）** 是最常見值 ——
 *   包括 pi-retail 掛著 7892 件貨的「廚部」。`4/5/6/7/8` 三庫**零出現**。
 *
 *   ⚠️ 後端 model 註解那套（6 小費、7 營業差額）**被資料反證**：真正的「小費」「營業差額」
 *   部門（pi-retail id=49/50）dept_type 都是 `0`，不是 6/7。後端亦對此欄零驗證、DB 無 CHECK。
 *   唯一有行為的值是 `1`（sales4/sales6 報表 join 條件，ReportController.cs:1966），
 *   但三庫掛 dept_type=1 的部門都是 0 件貨 → 該報表實質是死功能。
 *
 *   ★ 為何必須有 `None: 0`：舊版本的 enum 由 1 起，令 `isDeptType(0) === false`，
 *   10 列 dept_type=0 讀入即變 null，再經 fromDept 寫回就是靜默資料破壞（原缺陷，已修）。
 *   `0` 是合法且最常見的儲存值，不可當成非法值 fallback。
 *
 *   `4/5/6/7/8` 保留在列舉（讀到不 fallback），但新 UI 下拉**不提供**（標「保留未用」）。
 */
export const DeptType = {
  None: 0,
  Bread: 1,
  Drink: 2,
  Cashier: 3,
  Takeaway: 4,
  Other: 5,
  Tips: 6,
  SalesVariance: 7,
  PlasticBagTax: 8,
} as const
export type DeptType = (typeof DeptType)[keyof typeof DeptType]

export const deptTypeLabel: Record<DeptType, string> = {
  [DeptType.None]: '未指定',
  // 實測 1 唯一有後端行為（sales4/sales6 報表 tag），非真的「部門種類」
  [DeptType.Bread]: '麵包（報表用）',
  [DeptType.Drink]: '飲品',
  [DeptType.Cashier]: '收銀台',
  [DeptType.Takeaway]: '外賣',
  [DeptType.Other]: '其他',
  [DeptType.Tips]: '小費',
  [DeptType.SalesVariance]: '營業差額',
  [DeptType.PlasticBagTax]: '膠袋稅',
}

/** `merchant_print_setting.print_type` */
export const PrintType = {
  Thermal: 1,
  DotMatrix: 2,
} as const
export type PrintType = (typeof PrintType)[keyof typeof PrintType]

export const printTypeLabel: Record<PrintType, string> = {
  [PrintType.Thermal]: '熱敏打印機',
  [PrintType.DotMatrix]: '針式打印機',
}

/** `merchant_table.dinner_type` */
export const DinnerType = {
  DineIn: 0,
  Takeaway: 1,
  StaffMeal: 2,
} as const
export type DinnerType = (typeof DinnerType)[keyof typeof DinnerType]

export const dinnerTypeLabel: Record<DinnerType, string> = {
  [DinnerType.DineIn]: '堂食',
  [DinnerType.Takeaway]: '外賣',
  [DinnerType.StaffMeal]: '福食',
}

/**
 * `merchant_table.status`。
 * ⚠️ 後端 `[NotMapped] StatusName` 把 2 與 3 都顯示成「佔用」，遺失「離座埋單」。
 * 前端一律映射原始 int，不得使用 `StatusName`（文檔 01 §8.6）。
 */
export const TableStatus = {
  Vacant: 0,
  Reserved: 1,
  Seated: 2,
  AwaitingPayment: 3,
} as const
export type TableStatus = (typeof TableStatus)[keyof typeof TableStatus]

export const tableStatusLabel: Record<TableStatus, string> = {
  [TableStatus.Vacant]: '未入座',
  [TableStatus.Reserved]: '已預定',
  [TableStatus.Seated]: '已入座',
  [TableStatus.AwaitingPayment]: '離座埋單',
}

/* ────────────────────────────── M10 系統設定 ────────────────────────────── */

/** `merchant_task_email.email_type`：⚠️ 存的是字串本身，不是 id */
export const EmailType = {
  To: 'To',
  Cc: 'CC',
  Bcc: 'Bcc',
} as const
export type EmailType = (typeof EmailType)[keyof typeof EmailType]

export const emailTypeLabel: Record<EmailType, string> = {
  [EmailType.To]: '收件人',
  [EmailType.Cc]: '副本',
  [EmailType.Bcc]: '密件副本',
}

/*
 * 🚨 2026-08-24 移走了 `AppPlatform` / `AppName`（`admin_version.platform` / `.app`）。
 *    裝置版本更新已經由門店後台切去 ops-console 做全域管理（浸會回報第 5 條），
 *    shop-manager 不再讀寫 `admin_version`，保留這兩個列舉只會令人以為本專案仍管得到。
 *    新家：`ops-console/console/src/types.ts` 及 `control-plane/AppVersionEndpoints.cs`。
 */

/** `merchant_user.pos_type`：決定 i18n 詞彙包（文檔 06 §7）*/
export const PosType = {
  Food: 0,
  Retail: 1,
} as const
export type PosType = (typeof PosType)[keyof typeof PosType]

export const posTypeLabel: Record<PosType, string> = {
  [PosType.Food]: '餐飲',
  [PosType.Retail]: '零售',
}

/** `merchant_user.merchant_status`（平台審核流程遺留，後台唯讀顯示）*/
export const MerchantStatus = {
  PendingInfo: 0,
  PendingReview: 1,
  Trial: 2,
  Normal: 3,
} as const
export type MerchantStatus = (typeof MerchantStatus)[keyof typeof MerchantStatus]

export const merchantStatusLabel: Record<MerchantStatus, string> = {
  [MerchantStatus.PendingInfo]: '待完善資訊',
  [MerchantStatus.PendingReview]: '待審核',
  [MerchantStatus.Trial]: '審核通過試用',
  [MerchantStatus.Normal]: '正常',
}

/** `merchant_user.operating_status` */
export const OperatingStatus = {
  Closed: 0,
  Open: 1,
} as const
export type OperatingStatus = (typeof OperatingStatus)[keyof typeof OperatingStatus]

export const operatingStatusLabel: Record<OperatingStatus, string> = {
  [OperatingStatus.Closed]: '歇業中',
  [OperatingStatus.Open]: '營業中',
}

/*
 * `merchant_user.round_type`（整毫規則）：⚠️ 字串旗標，值即字面。
 *
 * ★ 2026-08-07 更正：**落庫值帶前綴減號**（範本庫實測 `-4+5`，
 *   `webapi/sql/store-template/food.sql`），本列舉原本寫 `'4+5'` 是錯的。
 *   消費者是 POS：`scpos/packages/vue/src/pages/settle-page.vue:611` 的
 *   `/^-(\d+)\+(\d+)$/` 對不上就當沒有設定 → **整毫靜默失效**（照原價收，沒有任何錯誤）。
 *   同一段碼（`:608`）記下 offset = (10 - Y) / 10，並且支援到 `-7+8`。
 */
export const RoundType = {
  /** 四捨五入 */
  HalfUp: '-4+5',
  /** 五捨六入 */
  HalfDown: '-5+6',
  /** 六捨七入 */
  RoundAt7: '-6+7',
  /** 七捨八入 */
  RoundAt8: '-7+8',
} as const
export type RoundType = (typeof RoundType)[keyof typeof RoundType]

export const roundTypeLabel: Record<RoundType, string> = {
  [RoundType.HalfUp]: '四捨五入',
  [RoundType.HalfDown]: '五捨六入',
  [RoundType.RoundAt7]: '六捨七入',
  [RoundType.RoundAt8]: '七捨八入',
}

/**
 * `merchant_user.round_scale`（20260910-hkbu §5）—— 埋單應付**捨到哪一位**。
 *
 * ★ 與 {@link RoundType} 是兩個不同維度，要一併查看才解讀得到一間店的捨入行為：
 *   `round_type` = 哪個數字開始入位（四捨五入／五捨六入）；
 *   `round_scale` = 入到哪一位（元／毫／仙）。
 *
 * 🚨 **`0` 不是「未設定」，是「元位」**，而且是這個功能之前的行為
 *   （scpos 舊 `applyRound()` 固定是 `Math.trunc()` 到整數）。所以這一欄
 *   落庫是 `NOT NULL DEFAULT 0`，UI 亦**不提供「未設定」這個選項** ——
 *   與 `round_type` 的「未設定（保持原狀）」不同，那個真的代表「不捨入」。
 *
 * 🚨 `round_type` 未設 = 完全不捨入，那時這一欄沒有意義（POS 會照 1 位小數收）。
 *
 * 對應後端 `InnerHelper.TWG_Round2(d, …, x)` 個 `x`。
 */
export const RoundScale = {
  /** 元位（整數） */
  Dollar: 0,
  /** 毫位（一位小數） */
  Dime: 1,
  /** 仙位（兩位小數） */
  Cent: 2,
} as const
export type RoundScale = (typeof RoundScale)[keyof typeof RoundScale]

export const ROUND_SCALE_VALUES: readonly RoundScale[] = [
  RoundScale.Dollar,
  RoundScale.Dime,
  RoundScale.Cent,
] as const

export const roundScaleLabel: Record<RoundScale, string> = {
  [RoundScale.Dollar]: '元位（整數）',
  [RoundScale.Dime]: '毫位（一位小數）',
  [RoundScale.Cent]: '仙位（兩位小數）',
}

/** 落庫值 → 列舉。認不到一律回 `Dollar` —— 元位就是現狀，fallback 去第二個值會靜靜地改了應收。 */
export function parseRoundScale(v: unknown): RoundScale {
  const n = typeof v === 'string' ? Number(v.trim()) : Number(v)
  return n === RoundScale.Dime || n === RoundScale.Cent ? (n as RoundScale) : RoundScale.Dollar
}

/* ────────────────────────────── M9 權限 ────────────────────────────── */

/**
 * `merchant_function.type`。
 * ⚠️ 值 5（按鈕層級權限）**後端 model 註解未記載**，但後台實際使用中
 * （舊前端以 `type === 5` 篩出按鈕權限，文檔 04 §2.4）。刪除它會讓所有按鈕權限失效。
 */
export const FunctionType = {
  Menu: 1,
  Feature: 2,
  ExternalLink: 3,
  HiddenMenu: 4,
  Button: 5,
} as const
export type FunctionType = (typeof FunctionType)[keyof typeof FunctionType]

export const functionTypeLabel: Record<FunctionType, string> = {
  [FunctionType.Menu]: '選單',
  [FunctionType.Feature]: '功能',
  [FunctionType.ExternalLink]: '外部連結',
  [FunctionType.HiddenMenu]: '不顯示於選單樹的選單',
  [FunctionType.Button]: '按鈕權限',
}

/**
 * `merchant_function.code`：按鈕層級權限碼。
 * ⚠️ 值是後端既有資料，拼字錯誤（`updata`）與大小寫不一致（`Roles`）**都是實際值**，
 * 不得「順手修正」，否則權限比對全數落空。
 */
export const PermissionCode = {
  Update: 'updata',
  Add: 'add',
  Delete: 'delete',
  List: 'list',
  Bundle: 'bundle',
  Roles: 'Roles',
  Reset: 'reset',
  Change: 'change',
} as const
export type PermissionCode = (typeof PermissionCode)[keyof typeof PermissionCode]

export const permissionCodeLabel: Record<PermissionCode, string> = {
  [PermissionCode.Update]: '修改',
  [PermissionCode.Add]: '增加',
  [PermissionCode.Delete]: '刪除',
  [PermissionCode.List]: '上架',
  [PermissionCode.Bundle]: '口味及套餐',
  [PermissionCode.Roles]: '角色管理',
  [PermissionCode.Reset]: '重置密碼',
  [PermissionCode.Change]: '更改圖片',
}

/**
 * `admin_employee.custom_flag` 為此值時視為超級使用者，取得全部 functions
 * （文檔 02 §1.7）。⚠️ 是字串 `"99"`，不是數字。
 */
export const SUPER_USER_CUSTOM_FLAG = '99'

/* ────────────────────────────── 通用小工具 ────────────────────────────── */

/**
 * 從 `as const` 列舉物件建立「值 → 是否合法」的檢查器。
 * 用途：後端欄位可空且無約束，adapter 需在邊界判斷讀到的值是否落在已知列舉內；
 * 落在外面時應顯示原始值而非硬轉，避免靜默竄改資料。
 */
export function makeEnumGuard<T extends Record<string, string | number>>(
  source: T,
): (value: unknown) => value is T[keyof T] {
  const values = new Set<string | number>(Object.values(source))
  return (value: unknown): value is T[keyof T] =>
    (typeof value === 'number' || typeof value === 'string') && values.has(value)
}

const isBuiltinMenuGroup = makeEnumGuard(MenuGroup)
/** 固定渠道或自定義渠道（30–39）。見上面 `MenuGroup` 型別那段註解 —— 認不到會令組號被寫成 NULL。 */
export const isMenuGroup = (value: unknown): value is MenuGroup =>
  isBuiltinMenuGroup(value) || isCustomMenuGroup(value)
export const isPackageFlag = makeEnumGuard(PackageFlag)
export const isPaymentFlag = makeEnumGuard(PaymentFlag)
export const isGoodsStatus = makeEnumGuard(GoodsStatus)
export const isGoodsUnit = makeEnumGuard(GoodsUnit)
export const isGoodsAttribute = makeEnumGuard(GoodsAttribute)
export const isSetmealMainType = makeEnumGuard(SetmealMainType)
export const isDeptType = makeEnumGuard(DeptType)
export const isPrintType = makeEnumGuard(PrintType)
export const isDinnerType = makeEnumGuard(DinnerType)
export const isTableStatus = makeEnumGuard(TableStatus)
export const isEmailType = makeEnumGuard(EmailType)
export const isPosType = makeEnumGuard(PosType)
export const isFunctionType = makeEnumGuard(FunctionType)
export const isPermissionCode = makeEnumGuard(PermissionCode)
export const isRoundType = makeEnumGuard(RoundType)
